import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Truck, User, MessageCircle, ArrowLeft } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import AccessibleModal from '../components/AccessibleModal';

const HomePage = () => {
  const DISCLAIMER_KEY = 'disclaimer_ack_v1';
  const PROMO_KEY = 'driver_promo_seen_v1';
  const [trackCode, setTrackCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [showDriverPromo, setShowDriverPromo] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [enableTrialPeriod, setEnableTrialPeriod] = useState(null); // null = loading, will be set from API
  const navigate = useNavigate();
  const verificationInputRef = useRef(null);

  // Callback ref to handle focus
  const setVerificationInputRef = (node) => {
    verificationInputRef.current = node;
    if (node && showVerification) {
      // Focus immediately when the ref is set
      setTimeout(() => {
        node.focus();
      }, 100);
    }
  };

  const handleTrackSubmit = async (e) => {
    e.preventDefault();
    const trimmed = trackCode.trim();
    if (!trimmed) return;
    
    const requestId = trimmed.toUpperCase();
    const storedToken = sessionStorage.getItem(`request_session_${requestId}`);
    if (storedToken) {
      navigate(`/track/${requestId}`);
      return;
    }

    // No valid session token: require verification BEFORE navigation
    setShowVerification(true);
  };

  const sendVerificationCode = async () => {
    if (!trackCode.trim()) {
      toast.error('יש להזין מספר הזמנה תחילה');
      return;
    }

    setIsSendingCode(true);
    try {
      // First get the request to get the phone number
      const response = await axios.get(`${API_ENDPOINTS.REQUESTS}/track/${trackCode.trim().toUpperCase()}`);
      const request = response.data.data;
      
      await axios.post(`${API_ENDPOINTS.SMS}/send-verification`, {
        phone: request.customerPhone
      });
      
      toast.success('נשלח קוד אימות לטלפון שלך');
    } catch (error) {
      toast.error(error.response?.data?.message || 'שגיאה בשליחת קוד האימות');
    } finally {
      setIsSendingCode(false);
    }
  };

  useEffect(() => {
    // Fetch trial period configuration
    const fetchTrialConfig = async () => {
      try {
        const response = await axios.get(`${API_ENDPOINTS.CONFIG}/trial-enabled`);
        if (response.data.success && response.data.data) {
          setEnableTrialPeriod(response.data.data.enableTrialPeriod);
        } else {
          // Default to false if API response is invalid
          setEnableTrialPeriod(false);
        }
      } catch (error) {
        // If API call fails, default to false (trial disabled)
        console.error('Failed to fetch trial config:', error);
        setEnableTrialPeriod(false);
      }
    };

    fetchTrialConfig();
  }, []);

  useEffect(() => {
    const hasAcknowledgedDisclaimer = localStorage.getItem(DISCLAIMER_KEY) === 'true';
    
    // Always check disclaimer first, regardless of trial period status
    if (!hasAcknowledgedDisclaimer) {
      setShowDisclaimer(true);
      return;
    }

    // Only check for promo modal after we know the trial period status
    if (enableTrialPeriod === null) return; // Still loading

    // If trial period is disabled, ensure modal is not shown
    if (enableTrialPeriod === false) {
      setShowDriverPromo(false);
      return;
    }

    const hasSeenDriverPromo = localStorage.getItem(PROMO_KEY) === 'true';

    // Only show promo modal if trial period is enabled AND user hasn't seen it
    if (enableTrialPeriod === true && !hasSeenDriverPromo) {
      setShowDriverPromo(true);
    }
  }, [enableTrialPeriod]);


  const acknowledgeDisclaimer = () => {
    localStorage.setItem(DISCLAIMER_KEY, 'true');
    setShowDisclaimer(false);

    // Only show promo modal if trial period is enabled
    const hasSeenDriverPromo = localStorage.getItem(PROMO_KEY) === 'true';
    if (enableTrialPeriod === true && !hasSeenDriverPromo) {
      setShowDriverPromo(true);
    }
  };

  const verifyCode = async () => {
    if (!verificationCode.trim()) {
      toast.error('יש להזין קוד אימות');
      return;
    }

    setIsVerifying(true);
    try {
      // First get the request to get the phone number
      const requestId = trackCode.trim().toUpperCase();
      const response = await axios.get(`${API_ENDPOINTS.REQUESTS}/track/${requestId}`);
      const request = response.data.data;
      
      // Use GET endpoint for verification (viewing only)
      const verifyRes = await axios.get(`${API_ENDPOINTS.REQUESTS}/${requestId}/verify-customer`, {
        params: {
          phone: request.customerPhone,
          code: verificationCode
        }
      });
      
      const token = verifyRes.data.data.sessionToken;
      sessionStorage.setItem(`request_session_${requestId}`, token);
      setShowVerification(false);
      setVerificationCode('');
      toast.success('מספר הטלפון אומת בהצלחה');
      navigate(`/track/${requestId}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'קוד האימות שגוי');
    } finally {
      setIsVerifying(false);
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50">
      {/* Header */}
      <header id="navigation" className="header-gradient shadow-lg" role="banner" tabIndex="-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <BrandLogo className="h-10 md:h-14 w-auto" />
              <h1 className="text-3xl font-bold text-white mr-4">מי פנוי</h1>
            </div>
            <nav className="flex space-x-4 space-x-reverse" role="navigation" aria-label="ניווט ראשי">
              <Link
                to="/driver/login"
                className="text-white hover:text-orange-100 font-semibold text-lg transition-colors duration-300 border-2 border-white hover:border-orange-100 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-orange-500"
              >
                התחברות מובילים
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" role="main" tabIndex="-1">
        <div className="text-center">
          <h2 className="text-5xl font-bold text-brown-900 mb-8">
            הזמנת הובלות דירה
          </h2>
          <p className="text-xl text-brown-700 mb-12 max-w-3xl mx-auto leading-relaxed">
            חיבור ישיר בין לקוחות למובילים מקצועיים. הזמן הובלה בקלות ובמהירות
          </p>
          
          <div className="flex justify-center space-x-4 space-x-reverse mb-16">
            <Link
              to="/request"
              className="btn-primary text-xl px-10 py-4 flex items-center hover-lift focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
            >
              הזמן הובלה
              <ArrowLeft className="mr-3 h-6 w-6" aria-hidden="true" />
            </Link>
          </div>

          {/* Tracking box */}
          <div className="max-w-xl mx-auto">
            <div className="card hover-lift">
              <h3 className="text-2xl font-bold text-brown-900 mb-6 text-center">מעקב אחרי הזמנה</h3>
              <form onSubmit={handleTrackSubmit} className="flex gap-4" role="search" aria-label="חיפוש הזמנה">
                <label htmlFor="track-code" className="sr-only">מספר הזמנה</label>
                <input
                  id="track-code"
                  type="text"
                  inputMode="text"
                  dir="ltr"
                  maxLength={50}
                  className="input-field flex-1"
                  placeholder="מספר הזמנה (לדוגמה: REQ240901234)"
                  value={trackCode}
                  onChange={(e) => setTrackCode(e.target.value)}
                  aria-describedby="track-help"
                />
                <button type="submit" className="btn-secondary whitespace-nowrap">מעקב</button>
              </form>
              <div id="track-help" className="text-sm text-gray-600 mt-2 text-center">
                הזן את מספר ההזמנה שקיבלת במייל או בהודעת SMS
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <section className="grid md:grid-cols-3 gap-10 mt-20" aria-label="תכונות השירות">
          <article className="card text-center flex flex-col h-full hover-lift">
            <div className="flex justify-center mb-6">
              <div className="bg-orange-100 p-4 rounded-full">
                <User className="h-16 w-16 text-orange-600" aria-hidden="true" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-brown-900 mb-4">
              ללקוחות
            </h3>
            <p className="text-brown-700 mb-6 flex-grow text-lg leading-relaxed">
              הזמן הובלה בקלות, קבל הצעות מחיר ממובילים מקצועיים ובחר את הטוב ביותר עבורך
            </p>
            <div className="flex justify-center mt-auto">
              <Link
                to="/request"
                className="btn-primary inline-flex items-center px-8 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              >
                הזמן עכשיו
                <ArrowLeft className="mr-3 h-5 w-5" aria-hidden="true" />
              </Link>
            </div>
          </article>

          <article className="card text-center flex flex-col h-full hover-lift">
            <div className="flex justify-center mb-6">
              <div className="bg-orange-100 p-4 rounded-full">
                <Truck className="h-16 w-16 text-orange-600" aria-hidden="true" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-brown-900 mb-4">
              למובילים
            </h3>
            <p className="text-brown-700 mb-6 flex-grow text-lg leading-relaxed">
              הצטרף למערכת, קבל בקשות הובלה והגדל את העסק שלך
            </p>
            <div className="flex justify-center mt-auto">
              <Link
                to="/driver/register"
                className="btn-primary inline-flex items-center px-8 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              >
                הצטרף עכשיו
                <ArrowLeft className="mr-3 h-5 w-5" aria-hidden="true" />
              </Link>
            </div>
          </article>

          <article className="card text-center flex flex-col h-full hover-lift">
            <div className="flex justify-center mb-6">
              <div className="bg-orange-100 p-4 rounded-full">
                <MessageCircle className="h-16 w-16 text-orange-600" aria-hidden="true" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-brown-900 mb-4">
              צור קשר
            </h3>
            <p className="text-brown-700 mb-6 flex-grow text-lg leading-relaxed">
              יש לך שאלה, בעיה או הצעה לשיפור? אנחנו כאן לעזור לך בכל דבר שתצטרך
            </p>
            <div className="flex justify-center mt-auto">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center px-8 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
              >
                צור קשר עכשיו
                <ArrowLeft className="mr-3 h-5 w-5" aria-hidden="true" />
              </Link>
            </div>
          </article>
        </section>

        {/* How it works */}
        <section className="mt-24" aria-label="איך זה עובד">
          <h3 className="text-4xl font-bold text-center text-brown-900 mb-16">
            איך זה עובד?
          </h3>
          
          <div className="grid md:grid-cols-4 gap-10">
            <div className="text-center">
              <div className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg" aria-hidden="true">
                <span className="text-3xl font-bold text-white">1</span>
              </div>
              <h4 className="text-xl font-bold text-brown-900 mb-3">
                מלא פרטים
              </h4>
              <p className="text-brown-700 text-lg leading-relaxed">
                הזן את פרטי ההובלה שלך בטופס פשוט
              </p>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg" aria-hidden="true">
                <span className="text-3xl font-bold text-white">2</span>
              </div>
              <h4 className="text-xl font-bold text-brown-900 mb-3">
                קבל הצעות
              </h4>
              <p className="text-brown-700 text-lg leading-relaxed">
                מובילים מקצועיים ישלחו לך הצעות מחיר
              </p>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg" aria-hidden="true">
                <span className="text-3xl font-bold text-white">3</span>
              </div>
              <h4 className="text-xl font-bold text-brown-900 mb-3">
                בחר מוביל
              </h4>
              <p className="text-brown-700 text-lg leading-relaxed">
                בחר את המוביל המתאים ביותר עבורך
              </p>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 shadow-lg" aria-hidden="true">
                <span className="text-3xl font-bold text-white">4</span>
              </div>
              <h4 className="text-xl font-bold text-brown-900 mb-3">
                הובלה מוצלחת
              </h4>
              <p className="text-brown-700 text-lg leading-relaxed">
                המוביל יגיע בזמן ויבצע את ההובלה
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer id="footer" className="bg-gradient-to-r from-brown-800 to-brown-900 text-white mt-20" role="contentinfo" tabIndex="-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="flex justify-center items-center mb-6">
              <Truck className="h-8 w-8 text-orange-400 mr-3" aria-hidden="true" />
              <h4 className="text-2xl font-bold text-orange-100">מי פנוי</h4>
            </div>
            <p className="text-brown-200 text-lg">
              © 2025 מי פנוי - הזמנת הובלות דירה. כל הזכויות שמורות.
            </p>
            <div className="mt-4">
              <Link 
                to="/accessibility" 
                className="text-orange-300 hover:text-orange-200 underline text-sm"
              >
                הצהרת נגישות
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Driver Promo Modal - first load only */}
      <AccessibleModal
        isOpen={showDriverPromo && enableTrialPeriod === true}
        onClose={() => {
          localStorage.setItem(PROMO_KEY, 'true');
          setShowDriverPromo(false);
        }}
        title={<>מובילים?<br />קבלו 90 ימי ניסיון בחינם</>}
        centerTitle
        className="text-center"
      >
        <p className="text-brown-700 mb-6 text-center text-lg">
          הצטרפו ל׳מי פנוי׳ וקבלו בקשות הובלה מאומתות, ניהול הצעות חכם והתראות בזמן אמת — ללא הגבלה וללא עלות ב-90 הימים הראשונים.
        </p>
        <div className="flex space-x-3 space-x-reverse">
          <button
            onClick={() => {
              localStorage.setItem(PROMO_KEY, 'true');
              setShowDriverPromo(false);
            }}
            className="btn-secondary flex-1"
          >
            לא עכשיו
          </button>
          <button
            onClick={() => {
              localStorage.setItem(PROMO_KEY, 'true');
              setShowDriverPromo(false);
              navigate('/driver/register');
            }}
            className="btn-primary flex-1"
          >
            הצטרפו בחינם
          </button>
        </div>
      </AccessibleModal>

      {/* Phone Verification Modal */}
      <AccessibleModal
        isOpen={showVerification}
        onClose={() => setShowVerification(false)}
        title="אימות מספר טלפון"
        disableFocusTrap={true}
      >
        <p className="text-brown-700 mb-6 text-center">
          כדי לגשת לעמוד המעקב, יש לאמת את מספר הטלפון שלך
        </p>
        <div className="space-y-4 relative z-30">
          <div className="flex space-x-3 space-x-reverse">
            <button
              onClick={sendVerificationCode}
              disabled={isSendingCode}
              className="btn-secondary flex-1 relative z-30"
              type="button"
            >
              {isSendingCode ? 'שולח...' : 'שלח קוד אימות'}
            </button>
          </div>
          <div className="relative z-30">
            <label htmlFor="verification-code" className="sr-only">קוד אימות</label>
            <input
              ref={setVerificationInputRef}
              id="verification-code"
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="הזן קוד אימות"
              className="input-field text-center text-2xl tracking-widest relative z-30"
              maxLength={6}
              aria-describedby="verification-help"
              autoComplete="one-time-code"
            />
            <div id="verification-help" className="text-sm text-gray-600 mt-1 text-center">
              הזן את הקוד בן 6 הספרות שנשלח לטלפון שלך
            </div>
          </div>
          <div className="flex space-x-3 space-x-reverse">
            <button
              onClick={() => setShowVerification(false)}
              className="btn-secondary flex-1 relative z-30"
              type="button"
            >
              ביטול
            </button>
            <button
              onClick={verifyCode}
              disabled={isVerifying}
              className="btn-primary flex-1 relative z-30"
              type="button"
            >
              {isVerifying ? 'מאמת...' : 'אמת'}
            </button>
          </div>
        </div>
      </AccessibleModal>

      {/* First-load Disclaimer Modal */}
      <AccessibleModal
        isOpen={showDisclaimer}
        onClose={acknowledgeDisclaimer}
        title="היי!"
        closeOnOverlayClick={false}
        closeOnEscape={false}
        centerTitle={true}
      >
        <p className="text-brown-700 mb-6 text-center">
        האתר בשלבי פיתוח סופיים, אז אם משהו מתקלקל - ספרו לנו בטופס הפניות בעמוד הבית.
        </p>
        <div className="flex space-x-3 space-x-reverse">
          <button onClick={acknowledgeDisclaimer} className="btn-primary flex-1">
            הבנתי
          </button>
        </div>
      </AccessibleModal>
    </div>
  );
};

export default HomePage;
